import { useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Injects a supplementary BarberShop JSON-LD that carries the LIVE
 * aggregateRating + up to 5 top reviews (fetched from /api/reviews,
 * which proxies the Google Places API and caches 6h). Google merges
 * multiple JSON-LD blocks for the same entity, so this enriches the
 * static schema in index.html with star-rating eligibility for the
 * Google local pack & rich results.
 *
 * Mount once at app root.
 */
const ReviewsSchemaInjector = () => {
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews`);
        if (cancelled) return;
        if (!data || !data.overall_rating || !data.total_reviews) return;

        const topReviews = (data.reviews || []).slice(0, 5).map((r) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.author_name },
          datePublished: r.time_str || undefined,
          reviewBody: r.text || '',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: r.rating,
            bestRating: 5,
            worstRating: 1,
          },
        }));

        const schema = {
          '@context': 'https://schema.org',
          '@type': ['BarberShop', 'LocalBusiness'],
          name: 'Casanova Salon Barbier',
          url: 'https://www.casanovamtl.com/',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: Number(data.overall_rating).toFixed(1),
            reviewCount: data.total_reviews,
            bestRating: 5,
            worstRating: 1,
          },
          ...(topReviews.length ? { review: topReviews } : {}),
        };

        let el = document.head.querySelector(
          'script[data-seo-reviews="1"]'
        );
        if (!el) {
          el = document.createElement('script');
          el.type = 'application/ld+json';
          el.setAttribute('data-seo-reviews', '1');
          document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(schema);
      } catch (e) {
        // Non-fatal — the static schema in index.html still covers us.
        // eslint-disable-next-line no-console
        console.warn('Reviews schema injection skipped', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
};

export default ReviewsSchemaInjector;
